import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonChip,
  IonCol,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonNote,
  IonSearchbar,
} from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useRoles } from "../data/store/useRoles";
import { ArtistSearchResult } from "../data/store/useSearch";
import { SearchService } from "../services/SearchService";

import isEmpty from "lodash.isempty";
import { Role, RoleType } from "../models/Role";

import { AsyncArtistByIdButton } from "./AsyncArtistButton";
import ChangeUserRole from "./ChangeUserRole";

// Search-and-pick a single artist by alias. Mirrors TransferOwnership's
// ArtistPicker — kept local/duplicated rather than shared since the two
// pickers' selection UI (chip vs. inline result list) are the only parts
// that would need to diverge if either grows more fields later.
const ArtistPicker: React.FC<{
  selected: ArtistSearchResult | null;
  onSelect: (artist: ArtistSearchResult | null) => void;
}> = ({ selected, onSelect }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ArtistSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!query || query.length < 1) {
      setResults([]);
      return;
    }
    const reqId = ++reqIdRef.current;
    setSearching(true);
    SearchService.please()
      .searchArtists(query, 0)
      .then(({ artists }) => {
        if (reqId !== reqIdRef.current) return;
        setResults((artists || []).filter((a) => a.alias && a.alias.length > 1));
      })
      .catch(() => {
        if (reqId !== reqIdRef.current) return;
        setResults([]);
      })
      .finally(() => {
        if (reqId !== reqIdRef.current) return;
        setSearching(false);
      });
  }, [query]);

  if (selected) {
    return (
      <IonItem lines="none">
        <IonLabel color="primary">Artist</IonLabel>
        <IonChip onClick={() => onSelect(null)}>
          <IonLabel>{selected.alias}</IonLabel>
          <IonIcon icon={closeOutline} aria-label="Clear selection" />
        </IonChip>
      </IonItem>
    );
  }

  return (
    <>
      <IonItem lines="none">
        <IonSearchbar
          value={query}
          debounce={250}
          placeholder="Search artists by alias"
          aria-label="Search artists by alias"
          onIonChange={(e) => setQuery(e.detail.value || "")}
        />
      </IonItem>
      {query.length > 0 && (
        <IonList>
          {searching && results.length === 0 && (
            <IonItem lines="none">
              <IonNote>Searching…</IonNote>
            </IonItem>
          )}
          {!searching && results.length === 0 && (
            <IonItem lines="none">
              <IonNote>No matches</IonNote>
            </IonItem>
          )}
          {results.slice(0, 8).map((artist) => (
            <IonItem
              key={artist.id}
              button
              onClick={() => {
                onSelect(artist);
                setQuery("");
                setResults([]);
              }}
            >
              <IonLabel>{artist.alias}</IonLabel>
              <IonNote slot="end" style={{ fontSize: 12 }}>
                {artist.id}
              </IonNote>
            </IonItem>
          ))}
        </IonList>
      )}
    </>
  );
};

export const RolesPanel: React.FC = () => {
  const { getUsersWithRoles, allUsersWithRoles, role } = useRoles();
  const [grantTarget, setGrantTarget] = useState<ArtistSearchResult | null>(null);

  const getUsers = (roles: RoleType) =>
    allUsersWithRoles.filter((item: Role) => item.authority.includes(roles));
  const allSuperAdmins = getUsers("super admin");
  const allAdmins = getUsers("admin");
  const allModerators = getUsers("moderator");

  const authority = role && role.authority;
  const isSuperAdmin = authority && authority.includes("super admin");
  const isAdmin = authority && authority.includes("admin");
  useEffect(() => {
    getUsersWithRoles();
  }, [getUsersWithRoles]);

  const superAdmins = useMemo(
    () =>
      allSuperAdmins.map((item: Role) => {
        return (
          <IonItem key={item.ownerId}>
            <IonLabel>
              <AsyncArtistByIdButton variant="alias" id={item.id} />
            </IonLabel>
            {isSuperAdmin && (
              <IonCol sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">
                <ChangeUserRole id={item.id} />
              </IonCol>
            )}
          </IonItem>
        );
      }),
    [allSuperAdmins,isSuperAdmin]
  );
  return (
    <>
      {(isAdmin || isSuperAdmin) && (
        <IonCard>
          <IonCardHeader color="primary">
            <IonCardTitle color="secondary">Grant a Role</IonCardTitle>
            <IonCardSubtitle>
              Find any artist and give them a first role — the lists below only
              cover people who already have one.
            </IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent>
            <ArtistPicker selected={grantTarget} onSelect={setGrantTarget} />
            {grantTarget && (
              <IonCol sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">
                <ChangeUserRole id={grantTarget.id} />
              </IonCol>
            )}
          </IonCardContent>
        </IonCard>
      )}

      <IonCard>
      <IonCardHeader color="primary">
        <IonCardTitle color="secondary">Permissions</IonCardTitle>
      </IonCardHeader>

      <IonCardContent>
        {!isEmpty(allSuperAdmins) && (
          <IonList>
            <IonCardSubtitle style={{ margin: "10px 0" }}>
              Super Admins:
            </IonCardSubtitle>
            {superAdmins}
            <IonItemDivider color={"clear"} />
          </IonList>
        )}

        {!isEmpty(allAdmins) && (
          <IonList>
            <IonCardSubtitle style={{ margin: "10px 0" }}>
              Admins:
            </IonCardSubtitle>
            {allAdmins.map((item: Role) => {
              return (
                <IonItem key={item.ownerId}>
                  <IonLabel>
                    <AsyncArtistByIdButton variant="alias" id={item.id} />
                  </IonLabel>
                  {isSuperAdmin && (
                    <IonCol sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">
                      <ChangeUserRole id={item.id} />
                    </IonCol>
                  )}
                </IonItem>
              );
            })}
            <IonItemDivider color={"clear"} />
          </IonList>
        )}

        {!isEmpty(allModerators) && (
          <IonList>
            <IonCardSubtitle style={{ margin: "10px 0" }}>
              Moderators:
            </IonCardSubtitle>
            {allModerators.map((item: Role) => {
              return (
                <IonItem key={item.ownerId}>
                  <IonLabel>
                    <AsyncArtistByIdButton variant="alias" id={item.id} />
                  </IonLabel>

                  {(isSuperAdmin || isAdmin) && (
                    <IonCol sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">
                      <ChangeUserRole id={item.id} />
                    </IonCol>
                  )}
                </IonItem>
              );
            })}
            <IonItemDivider color="clear" />
          </IonList>
        )}
      </IonCardContent>
    </IonCard>
    </>
  );
};

export default RolesPanel;
